import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import type { IAuthInitializerProps } from './AuthInitializer.types'

import { useUserMetadata } from '@/hooks/useUserMetadata'
import { useAuthMonitor } from '@/hooks/useAuthMonitor'
import { logger } from '@/utils/logger'
import { hasValidTokens, clearAllTokens } from '@/utils/auth/tokenManager'
import { updateUserData, clearAuth, setLoading, setMockAuthData } from '@/store/slices/authSlice'
import { Queries } from '@/api/queries.enum'
import type { RootState } from '@/store/store'
import { clearOptimoUserContext, setOptimoUserContext } from '@/lib/sentry'
import { isSkipAuthAllowed } from '@/utils/auth/skipAuth'

export const AuthInitializer = ({ children }: IAuthInitializerProps) => {
    const dispatch = useDispatch()
    const queryClient = useQueryClient()
    const location = useLocation()
    const hasInitialized = useRef(false)
    const {
        user: existingUser,
        isAuthenticated,
        isMockAuth,
        organization,
    } = useSelector((state: RootState) => state.auth)

    // Enable unified authentication monitoring
    useAuthMonitor()

    // Auto-skipAuth: Check if skipAuth parameter is in URL and automatically set mock auth
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search)
        const skipAuthRequested = urlParams.get('skipAuth') === 'true'

        // Only set mock auth if:
        // 1. skipAuth=true in URL
        // 2. skipAuth is allowed (returns true for all environments)
        // 3. We don't already have a user
        // 4. We're not already using mock auth
        if (skipAuthRequested && isSkipAuthAllowed() && !existingUser && !isMockAuth) {
            logger.info('Auto-skipAuth: Setting mock manager auth data')
            // Default to 'manager' role for demo
            dispatch(setMockAuthData('manager'))
        }
    }, [dispatch, existingUser, isMockAuth])

    // Check user metadata on app startup - this will fetch if we have tokens
    const { data: userData, isLoading: metadataLoading, error, isError } = useUserMetadata()

    useEffect(() => {
        if (!existingUser || !isAuthenticated || isMockAuth) {
            clearOptimoUserContext()
            return
        }

        setOptimoUserContext(existingUser, organization)
    }, [existingUser, organization, isAuthenticated, isMockAuth])

    useEffect(() => {
        // Prevent multiple initializations
        if (hasInitialized.current) return

        // Skip token validation if using mock auth
        if (isMockAuth) {
            logger.info('Mock auth mode - skipping token validation')
            hasInitialized.current = true
            dispatch(setLoading(false))
            return
        }

        // Only run once when metadata loading completes
        if (metadataLoading) return

        hasInitialized.current = true

        // Handle successful user data fetch
        if (userData && !existingUser) {
            dispatch(updateUserData(userData))
            dispatch(setLoading(false))
        }
        // Handle auth failure - clear tokens and auth state
        else if (isError || !hasValidTokens()) {
            clearAllTokens()
            dispatch(clearAuth())
        }
        // If we already have a user, just set loading to false
        else if (existingUser) {
            dispatch(setLoading(false))
        }
    }, [metadataLoading, userData, isError, existingUser, dispatch, isMockAuth])

    useEffect(() => {
        // Log authentication initialization
        if (logger && logger.info) {
            logger.info('Auth initialization', {
                hasUser: !!userData,
                isLoading: metadataLoading,
                hasError: !!error,
                hasTokens: hasValidTokens(),
            })
        }
    }, [userData, metadataLoading, error])

    // Route change monitoring - refetch user data on route changes (like Diversio-Frontend)
    useEffect(() => {
        if (isMockAuth) {
            logger.info('Route changed - skipping metadata refetch for mock auth')
            clearOptimoUserContext()
            return
        }

        if (isAuthenticated && hasValidTokens()) {
            logger.info('Route changed - refetching user metadata', {
                pathname: location.pathname,
            })
            queryClient.invalidateQueries({ queryKey: [Queries.getUserMetadata] })

            if (existingUser) {
                setOptimoUserContext(existingUser, organization)
            }
        }
    }, [location.pathname, isAuthenticated, existingUser, organization, queryClient, isMockAuth])

    // Always render children - auth checks are handled by ProtectedRoute
    return <>{children}</>
}
